import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useCreateForgottenPassword } from '@services/auth/mutations/useCreateForgottenPassword'
import { useForgottenPassword } from '@services/auth/mutations/useForgottenPassword'

import { AuthLayout } from '../components/AuthLayout'
import { AuthSuccess } from '../components/AuthSuccess'
import { PasswordForm, PasswordFormValues } from '../components/PasswordForm'

export function ForgottenPassword() {
  const { t } = useTranslation('auth')
  const location = useLocation()
  const navigate = useNavigate()
  const token = new URLSearchParams(location.search).get('token')

  const {
    mutate: createForgottenPassword,
    isPending: isRequestPending,
    isSuccess: isRequestSuccess
  } = useCreateForgottenPassword()

  const {
    mutate: createNewPassword,
    isPending: isResetPending,
    isSuccess: isResetSuccess
  } = useForgottenPassword()

  useEffect(() => {
    if (isResetSuccess) navigate('/login')
  }, [isResetSuccess, navigate])

  const schema = useMemo(
    () => z.object({ email: z.string().email(t('forgotPassword.emailInvalid')) }),
    [t]
  )

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' }
  })

  const backToLogin = (
    <Link to="/login" className="font-semibold text-primary hover:underline">
      {t('register.login')}
    </Link>
  )

  // Step 2: reset password using the emailed token
  if (token) {
    const handleReset = (values: PasswordFormValues) =>
      createNewPassword({
        body: {
          token,
          password: values.password,
          password_confirmation: values.password_confirmation
        }
      })

    return (
      <AuthLayout
        title={t('forgotPassword.createPasswordTitle')}
        subtitle={t('forgotPassword.createPasswordText')}
        footer={backToLogin}
      >
        <PasswordForm onSubmit={handleReset} isPending={isResetPending} />
      </AuthLayout>
    )
  }

  // Request success screen
  if (isRequestSuccess) {
    return (
      <AuthSuccess
        title={t('forgotPassword.successTitle')}
        text={t('forgotPassword.successText')}
        footer={backToLogin}
      />
    )
  }

  // Step 1: request a reset link
  return (
    <AuthLayout
      title={t('forgotPassword.title')}
      subtitle={t('forgotPassword.text')}
      footer={backToLogin}
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((v) =>
            createForgottenPassword({ body: v })
          )}
          className="space-y-5"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('forgotPassword.email')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder={t('forgotPassword.emailPlaceholder')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={isRequestPending}
          >
            {t('forgotPassword.submit')}
          </Button>
        </form>
      </Form>
    </AuthLayout>
  )
}
